import { Flex, Box, Text, Img, forwardRef } from '@chakra-ui/react'
import { memo, useEffect, useMemo, useState } from 'react'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { useShallow } from 'zustand/react/shallow'

import { useRunesStore } from './RunesProvider'
import { GAME_RISK } from './types'

import StarInActive from '@/public/assets/images/runes/star_inactive.png'
import Star1InActive from '@/public/assets/images/runes/star_1_inactive.png'
import Star2InActive from '@/public/assets/images/runes/star2_inactive.png'
import { Image as ImageHOC } from '@/components/HOC/Image'
import { AnimatePresence, motion } from 'framer-motion'

type PaytableRow = {
  first: number
  second: number
  multiplier: {
    [key in GAME_RISK]: number
  }
}

const paytableData = [
  {
    first: 5,
    second: 0,
    multiplier: {
      [GAME_RISK.LOW]: 20,
      [GAME_RISK.MEDIUM]: 50,
      [GAME_RISK.HIGH]: 100,
    },
  },
  {
    first: 4,
    second: 1,
    multiplier: {
      [GAME_RISK.LOW]: 3.75,
      [GAME_RISK.MEDIUM]: 5.2,
      [GAME_RISK.HIGH]: 8,
    },
  },
  {
    first: 3,
    second: 2,
    multiplier: {
      [GAME_RISK.LOW]: 2.8,
      [GAME_RISK.MEDIUM]: 4,
      [GAME_RISK.HIGH]: 5.9,
    },
  },
  {
    first: 3,
    second: 1,
    multiplier: {
      [GAME_RISK.LOW]: 2.1,
      [GAME_RISK.MEDIUM]: 2.95,
      [GAME_RISK.HIGH]: 3.5,
    },
  },
  {
    first: 2,
    second: 2,
    multiplier: {
      [GAME_RISK.LOW]: 1.7,
      [GAME_RISK.MEDIUM]: 1.95,
      [GAME_RISK.HIGH]: 1.3,
    },
  },
  {
    first: 2,
    second: 1,
    multiplier: {
      [GAME_RISK.LOW]: 0.5,
      [GAME_RISK.MEDIUM]: 0.1,
      [GAME_RISK.HIGH]: 0,
    },
  },
  {
    first: 1,
    second: 1,
    multiplier: {
      [GAME_RISK.LOW]: 0.1,
      [GAME_RISK.MEDIUM]: 0,
      [GAME_RISK.HIGH]: 0,
    },
  },
]

export const Paytable = memo(() => {
  const { form } = useRunesStore()

  const gameResult = useRunesStore(useShallow((state) => state.view.gameResult.items))

  const items = gameResult

  const currentResult = useMemo(() => {
    if (gameResult.filter((item) => item == 0).length == 5 || gameResult.length < 2) {
      return paytableData.at(-1)
    }

    const itemCounts = gameResult.reduce(
      (acc, item) => {
        acc[item] = (acc[item] || 0) + 1
        return acc
      },
      {} as Record<number, number>
    )
    const sortedCounts = Object.entries(itemCounts)
      .map(([item, count]) => ({ item: parseInt(item), count }))
      .sort((a, b) => b.count - a.count)

    const maxCount = sortedCounts[0]?.count || 0
    const secondMaxCount = sortedCounts[1]?.count || 1

    const matchingEntry = paytableData.find((entry) => {
      if (entry.first === 0) {
        return maxCount === 0
      }
      return entry.first === maxCount && entry.second === secondMaxCount
    })

    return matchingEntry
  }, [items.join(',')])

  const mapColor = (value: number) => {
    if (value >= 10) return '0px 0px 20px -4px #4EFF60 inset;'
    if (value >= 3.5) return '0px 0px 20px -4px #C077F7 inset'
    if (value >= 1) return '0px 0px 20px -4px rgba(255, 255, 255, 0.80) inset'
    return '0px 0px 20px -4px #FF3288 inset'
  }
  return (
    <Flex flexDirection={'column'} alignItems={'center'}>
      <AnimatePresence>
        {paytableData.map((row, idx) => (
          <motion.div
            key={idx}
            variants={{
              inactive: {
                boxShadow: '0px 0px 0px 0px rgba(0, 0, 0, 0) inset',
              },
              active: {
                boxShadow: ' 0px -2px 16px 0px #C077F7 inset',
              },
            }}
            transition={{ duration: 0.5 }}
            animate={currentResult === row ? 'active' : 'inactive'}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '16px',
              marginTop: '2px',
            }}
            initial="inactive"
          >
            <Flex align="center" justify="left" w="100%" py={1} px={4} width={[300, 360]}>
              <Stars row={row} />
              <Box px={8}>
                <FiatOrCryptoAmount
                  value={TokenMoney.fromHuman(
                    Number(form.fields.amount || 0) * row.multiplier[form.fields.risk] || 0,
                    form.fields.denom
                  )}
                  amountStyles={{ fontSize: 'xs', fontWeight: 700, verticalAlign: '1px', lineHeight: '20px' }}
                  currencyStyles={{ display: 'none' }}
                />
              </Box>

              <Flex justifyContent="center" alignItems="center" flexWrap="wrap" ml="auto">
                <Flex
                  flexShrink={0}
                  gap={2.5}
                  py={{ base: 0, sm: 1.5 }}
                  w={'64px'}
                  bg={'rgba(255, 255, 255, 0.01)'}
                  boxShadow={mapColor(row.multiplier[form.fields.risk])}
                  rounded={{ base: 'md', sm: 'xl' }}
                  textAlign={'center'}
                  alignItems={'center'}
                  justifyContent="center"
                  fontWeight={700}
                  fontSize={'xs'}
                  cursor={'default'}
                  userSelect={'none'}
                >
                  x{row.multiplier[form.fields.risk].toFixed(2)}
                </Flex>
              </Flex>
            </Flex>
          </motion.div>
        ))}
      </AnimatePresence>
    </Flex>
  )
})

const Stars = ({ row }: { row: PaytableRow }) => {
  return (
    <Flex flexDir={'row'} gap={2} w={[100, 140]}>
      {row.first === 1 ? (
        <>
          {Array.from({ length: 5 }).map((_, i) => (
            <ImageHOC key={i} src={StarInActive} alt="" w={[3, 5]} h={[3, 5]} />
          ))}
        </>
      ) : (
        <>
          {Array.from({ length: row.first }).map((_, i) => (
            <ImageHOC key={i} src={Star1InActive} alt="" w={[3, 5]} h={[3, 5]} />
          ))}
          {row.second > 1 &&
            Array.from({ length: row.second }).map((_, i) => (
              <ImageHOC key={i} src={Star2InActive} alt="" w={[3, 5]} h={[3, 5]} />
            ))}
          {Array.from({
            length: row.second > 1 ? Math.max(0, 5 - row.first - row.second) : Math.max(0, 5 - row.first),
          }).map((_, i) => (
            <ImageHOC key={i} src={StarInActive} alt="" w={[3, 5]} h={[3, 5]} />
          ))}
        </>
      )}
    </Flex>
  )
}
